import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock as faRegularClock } from "@fortawesome/pro-regular-svg-icons";
import {
  faChevronUp as faSolidChevronUp,
  faChevronDown as faSolidChevronDown,
} from "@fortawesome/pro-solid-svg-icons";
import { ChangeHandler } from "react-hook-form";
import { isEmpty } from "lodash";
import classNames from "classnames";

type TimePickerArgs = {
  name: string;
  onChange: (value: string) => void | ChangeHandler;
  value?: string;
  ariaLabel?: string;
  disabled?: boolean;
  allowEmpty?: boolean;
  groupClassName?: string;
};

export default function TimePicker({
  value,
  onChange,
  ariaLabel,
  disabled = false,
  allowEmpty = false,
  name,
  groupClassName,
}: TimePickerArgs) {
  const [showPicker, setShowPicker] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(moment(value));
  const [inputValue, setInputValue] = useState(
    value
      ? moment(value).format("HH:mm")
      : allowEmpty
        ? ""
        : moment().format("HH:mm"),
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // an empty time that may stay empty is only written once somebody picks one
  const picked = useRef(false);

  useEffect(() => {
    setSelectedDateTime(moment(value)); // update date if its changed from outside (e.g. datepicker)
    // emptied from outside (e.g. a form reset): show it empty and wait for a new pick
    if (isEmpty(value) && allowEmpty) {
      picked.current = false;
      setInputValue("");
    } else if (!isEmpty(value) && moment(value).isValid()) {
      // show what gets saved, not a time the form no longer holds
      setInputValue(moment(value).format("HH:mm"));
    }
  }, [value, allowEmpty]);

  useEffect(() => {
    // without a date there is no time to write, only "Invalid date"
    if (!selectedDateTime.isValid()) return;
    if (isEmpty(value) && allowEmpty && !picked.current) return;
    // an empty required time takes the shown one, instead of only by chance of a millisecond
    if (isEmpty(value) || !selectedDateTime.isSame(moment(value))) {
      onChange(moment(selectedDateTime).format());
    }
  }, [selectedDateTime]);

  function handleClickOutside(event: MouseEvent) {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setShowPicker(false);
    }
  }

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  function increaseHour(e?: React.MouseEvent<HTMLButtonElement>) {
    e?.preventDefault();

    picked.current = true;
    const newDateTime = moment(selectedDateTime).add(1, "hour");
    setSelectedDateTime(newDateTime);
    setInputValue(newDateTime.format("HH:mm"));
  }

  function decreaseHour(e?: React.MouseEvent<HTMLButtonElement>) {
    e?.preventDefault();

    picked.current = true;
    const newDateTime = moment(selectedDateTime).subtract(1, "hour");
    setSelectedDateTime(newDateTime);
    setInputValue(newDateTime.format("HH:mm"));
  }

  function increaseMinutes(e?: React.MouseEvent<HTMLButtonElement>) {
    e?.preventDefault();

    picked.current = true;
    const minutes = selectedDateTime.minutes();
    const roundedMinutes = Math.floor(minutes / 5) * 5;

    selectedDateTime.minutes(roundedMinutes);

    const newDateTime = moment(selectedDateTime)
      .startOf("minute")
      .add(5, "minutes");
    setSelectedDateTime(newDateTime);
    setInputValue(newDateTime.format("HH:mm"));
  }

  function decreaseMinutes(e?: React.MouseEvent<HTMLButtonElement>) {
    e?.preventDefault();

    picked.current = true;
    const minutes = selectedDateTime.minutes();
    const roundedMinutes = Math.ceil(minutes / 5) * 5;

    selectedDateTime.minutes(roundedMinutes);

    const newDateTime = moment(selectedDateTime).subtract(5, "minutes");
    setSelectedDateTime(newDateTime);
    setInputValue(newDateTime.format("HH:mm"));
  }

  // "16:30", "16.30", "16,30", "16 30", "1630", "930", "16", "16 Uhr";
  // null for anything else, instead of quietly turning it into midnight
  function parseTime(value: string): [number, number] | null {
    const time = value.trim().replace(/\s*uhr$/i, "");
    const match =
      time.match(/^(\d{1,2})(?:\s*[:.,\s]\s*(\d{1,2}))?$/) ||
      time.match(/^(\d{1,2})(\d{2})$/);
    if (!match) return null;

    const hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    if (hours > 23 || minutes > 59) return null;

    return [hours, minutes];
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);
  }
  function handleBlur() {
    if (allowEmpty && (isEmpty(inputValue) || inputValue.trim() === "")) {
      // emptied again: stays empty until somebody picks a time anew
      picked.current = false;
      setInputValue("");
      onChange("");
      return;
    }

    const newTime = parseTime(inputValue);
    if (!newTime) {
      // not a time: show the one that stays
      setInputValue(
        (isEmpty(value) && allowEmpty && !picked.current) ||
          !selectedDateTime.isValid()
          ? ""
          : selectedDateTime.format("HH:mm"),
      );
      return;
    }

    picked.current = true;
    const [newHours, newMinutes] = newTime;

    const updatedDateTime = moment(selectedDateTime)
      .hours(newHours)
      .minutes(newMinutes);

    setSelectedDateTime(updatedDateTime);
    setInputValue(updatedDateTime.format("HH:mm"));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const input = e.target as HTMLInputElement;
    const { selectionStart, selectionEnd } = input;

    if (selectionStart === null || selectionEnd === null) {
      return;
    }

    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();

      if (selectionStart < 3) {
        if (e.key === "ArrowUp") {
          increaseHour();
        } else if (e.key === "ArrowDown") {
          decreaseHour();
        }
      } else {
        if (e.key === "ArrowUp") {
          increaseMinutes();
        } else if (e.key === "ArrowDown") {
          decreaseMinutes();
        }
      }

      setTimeout(() => {
        if (inputRef && inputRef.current) {
          inputRef.current.selectionStart = selectionStart;
          inputRef.current.selectionEnd = selectionEnd;
        }
      }, 0);
    }
  }

  return (
    <div className="time-picker-container w-28" ref={containerRef}>
      <div className={classNames("input-group w-28", groupClassName)}>
        <input
          ref={inputRef}
          name={name}
          value={inputValue}
          type="text"
          onFocus={() => setShowPicker(true)}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={I18n.t("js.calendars.date_range.time_placeholder")}
          aria-label={ariaLabel}
          disabled={disabled}
        />
        <button
          className="btn btn-light btn-sm"
          onClick={(e) => {
            e.preventDefault();
            inputRef?.current?.focus();
          }}
          role="button"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faRegularClock} />
        </button>
      </div>
      {showPicker && (
        <div className="border-box absolute mt-1 p-3 z-20">
          <div className="grid grid-cols-3 grid-rows-[1fr_25px_1fr] gap-y-2">
            <button
              className="btn btn-sm btn-light"
              onClick={increaseHour}
              disabled={disabled}
            >
              <FontAwesomeIcon icon={faSolidChevronUp} />
            </button>
            <div />
            <button
              className="btn btn-sm btn-light"
              onClick={increaseMinutes}
              disabled={disabled}
            >
              <FontAwesomeIcon icon={faSolidChevronUp} />
            </button>
            <div className="text-center align-baseline">
              {selectedDateTime.format("HH")}
            </div>
            <div className="text-center align-baseline">:</div>
            <div className="text-center align-baseline">
              {selectedDateTime.format("mm")}
            </div>
            <button
              className="btn btn-sm btn-light"
              onClick={decreaseHour}
              disabled={disabled}
            >
              <FontAwesomeIcon icon={faSolidChevronDown} />
            </button>
            <div />
            <button
              className="btn btn-sm btn-light"
              onClick={decreaseMinutes}
              disabled={disabled}
            >
              <FontAwesomeIcon icon={faSolidChevronDown} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
